/**
 * CollapsibleAnalytics — Wrap analytics panels with expand/collapse toggle
 *
 * - Persists preference in localStorage (treatx_dashboard_analytics_collapsed)
 * - Default: expanded on desktop, collapsed on mobile
 * - Collapsed state shows compact summary strip
 * - Smooth height animation <300ms
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge } from './ui/badge';
import {
  ChevronDown, ChevronUp, Activity, TrendingUp, TrendingDown, Minus
} from 'lucide-react';

const STORAGE_KEY = 'treatx_dashboard_analytics_collapsed';

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

const getInitialState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) return stored === 'true';
  } catch {}
  return isMobile();
};

export const CollapsibleAnalytics = ({
  children,
  conversionRate,
  conversionDelta,
  totalLeads,
  wonLeads,
  lostLeads,
  forceExpanded,
  forceCollapsed,
}) => {
  const [collapsed, setCollapsed] = useState(getInitialState);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState('auto');

  // Preset-driven override
  useEffect(() => {
    if (forceExpanded === true) setCollapsed(false);
    else if (forceCollapsed === true) setCollapsed(true);
  }, [forceExpanded, forceCollapsed]);

  // Persist preference
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(collapsed)); } catch {}
  }, [collapsed]);

  // Measure content height for animation
  useEffect(() => {
    if (contentRef.current) {
      const ro = new ResizeObserver(() => {
        if (!collapsed && contentRef.current) {
          setContentHeight(contentRef.current.scrollHeight + 'px');
        }
      });
      ro.observe(contentRef.current);
      return () => ro.disconnect();
    }
  }, [collapsed]);

  // Set initial height after first render
  useEffect(() => {
    if (!collapsed && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight + 'px');
    }
  }, [collapsed, children]);

  const toggle = useCallback(() => setCollapsed(prev => !prev), []);

  const isUp = conversionDelta > 0;
  const isDown = conversionDelta < 0;

  return (
    <div data-testid="collapsible-analytics">
      {/* Toggle bar */}
      <button
        onClick={toggle}
        className="w-full flex items-center gap-2 px-1 py-1.5 group"
        data-testid="analytics-toggle-btn"
      >
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 group-hover:text-slate-700 transition-colors">
          <Activity className="h-3 w-3" />
          {collapsed ? 'Show Analytics' : 'Hide Analytics'}
          {collapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
        </div>

        {/* Compact summary strip — visible when collapsed */}
        {collapsed && (
          <div className="flex items-center gap-3 ml-auto" data-testid="analytics-summary-strip">
            {totalLeads !== undefined && (
              <span className="text-[10px] text-slate-500">
                <span className="font-semibold text-slate-700">{totalLeads}</span> leads
              </span>
            )}
            {wonLeads !== undefined && (
              <span className="text-[10px] text-emerald-600 font-semibold">{wonLeads} won</span>
            )}
            {lostLeads !== undefined && (
              <span className="text-[10px] text-red-500 font-semibold">{lostLeads} lost</span>
            )}
            {conversionRate !== undefined && (
              <Badge variant="outline" className="text-[10px] font-bold h-5 px-1.5" data-testid="summary-conversion-rate">
                {conversionRate}%
              </Badge>
            )}
            {conversionDelta !== null && conversionDelta !== undefined && (
              <span
                className={`inline-flex items-center gap-px text-[10px] font-semibold ${
                  isUp ? 'text-emerald-600' : isDown ? 'text-red-500' : 'text-slate-400'
                }`}
                data-testid="summary-conversion-delta"
              >
                {isUp ? <TrendingUp className="h-2.5 w-2.5" /> : isDown ? <TrendingDown className="h-2.5 w-2.5" /> : <Minus className="h-2.5 w-2.5" />}
                {conversionDelta > 0 ? '+' : ''}{conversionDelta}%
              </span>
            )}
          </div>
        )}
      </button>

      {/* Animated content area */}
      <div
        style={{
          maxHeight: collapsed ? '0px' : contentHeight,
          opacity: collapsed ? 0 : 1,
          overflow: 'hidden',
          transition: 'max-height 280ms ease-in-out, opacity 200ms ease-in-out',
        }}
        data-testid="analytics-content"
      >
        <div ref={contentRef} className="space-y-3">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleAnalytics;
